import moment from 'moment';
import { getCssColors } from 'src/scss/theme';
import { formatMoneyWithCurrency } from 'src/utils/formatter';
import {
  formatDashboardMonthCode,
  formatDashboardLabelPeriod,
  formatPercentValue,
} from 'src/utils/dashboardMonthLabels';

// Función auxiliar para el corte de meses a mostrar.
// Siempre mostrar 12 meses fijos (Ene-Dic) para año actual y años pasados.
export function GetLastMonthWithData(source) {
    if (!source || source.length === 0) return null;
    // Devolver null = no aplicar filtro = mostrar los 12 meses completos
    return null;
}

// Función auxiliar para filtrar datos hasta el último mes comparable
export function FilterDataUpToMonth(source, lastMonthIndex) {
    if (!source || lastMonthIndex === null) return source;
    return source.slice(0, lastMonthIndex + 1);
}

export function GetLabels({ source, period, specificPeriods, lastMonthIndex = null, language = 'es' }) {

    if (!source) return []

    // Para vista anual, aplicar filtro de lastMonthIndex
    if (period === 'year') {
        const filteredSource = lastMonthIndex !== null ? FilterDataUpToMonth(source, lastMonthIndex) : source;
        return filteredSource.reduce((acc, el) => {
            const [month] = el.label.split('-')
            const monthName = formatDashboardMonthCode(month, language)
            if (acc.includes(monthName)) return acc
            acc.push(monthName)
            return acc
        },[])
    }

    // Para semestres y trimestres, primero filtrar por período y luego por lastMonthIndex
    let startIndex = 0;
    let endIndex = 12;
    
    if (period === 'semester') {
        startIndex = specificPeriods === 'firstSemester' ? 0 : 6;
        endIndex = specificPeriods === 'firstSemester' ? 6 : 12;
    } else if (period === 'quarter') {
        if (specificPeriods === 'firstQuarter') { startIndex = 0; endIndex = 3; }
        else if (specificPeriods === 'secondQuarter') { startIndex = 3; endIndex = 6; }
        else if (specificPeriods === 'thirdQuarter') { startIndex = 6; endIndex = 9; }
        else if (specificPeriods === 'fourthQuarter') { startIndex = 9; endIndex = 12; }
    }
    
    // Aplicar lastMonthIndex si existe
    if (lastMonthIndex !== null && lastMonthIndex < endIndex) {
        endIndex = lastMonthIndex + 1;
    }
    
    // Asegurar que startIndex no sea mayor que endIndex
    if (startIndex >= endIndex) {
        return [];
    }
    
    return source.slice(startIndex, endIndex).reduce((acc, el) => {
        const [month] = el.label.split('-')
        const monthName = formatDashboardMonthCode(month, language)
        if (acc.includes(monthName)) return acc
        acc.push(monthName)
        return acc
    }, [])
}

export function GetDataset({ source, period, specificPeriods, hidden = false, isPast = false, lastMonthIndex = null }) {

    const { black, green } = getCssColors();
    const seriesYear = isPast ? moment().subtract(1, 'year').year() : moment().year();

    if (!source || !Array.isArray(source) || source.length === 0) return {
        backgroundColor: Array.from({ length: 12 }, () => (isPast ? green : black)),
        hidden,
        label: Array.from({ length: 12 }, (_, i) =>
          moment({ year: seriesYear, month: i }).format('MMM-YYYY').replace(/\./g, "")
        ),
        data: Array.from({ length: 12 }, () => 0),
    }

    // Primero creamos el set completo con todos los 12 meses
    const fullSet = {
        backgroundColor: Array.from({ length: Math.max(source.length, 12) }, (_, i) => isPast ? green : black).slice(0, source.length),
        hidden,
        label: source.map(el => el.label),
        data: source.map(el => el.sales),
    }

    // Luego aplicamos el filtro de período (trimestre/semestre)
    let periodFilteredSet = fullSet;
    
    if (period === 'semester') {
        // Sin subperíodo aún: no vaciar el chart
        if (!specificPeriods) {
            return fullSet;
        }
        if (specificPeriods == 'firstSemester') {
            periodFilteredSet = {
                ...fullSet,
                backgroundColor: fullSet.backgroundColor.slice(0, 6),
                label: fullSet.label.slice(0, 6),
                data: fullSet.data.slice(0, 6),
            }
        } else if (specificPeriods == 'secondSemester') {
            periodFilteredSet = {
                ...fullSet,
                backgroundColor: fullSet.backgroundColor.slice(6, 12),
                label: fullSet.label.slice(6, 12),
                data: fullSet.data.slice(6, 12),
            }
        }
    } else if (period === 'quarter') {
        if (!specificPeriods) {
            return fullSet;
        }
        if (specificPeriods == 'firstQuarter') {
            periodFilteredSet = {
                ...fullSet,
                backgroundColor: fullSet.backgroundColor.slice(0, 3),
                label: fullSet.label.slice(0, 3),
                data: fullSet.data.slice(0, 3),
            }
        } else if (specificPeriods == 'secondQuarter') {
            periodFilteredSet = {
                ...fullSet,
                backgroundColor: fullSet.backgroundColor.slice(3, 6),
                label: fullSet.label.slice(3, 6),
                data: fullSet.data.slice(3, 6),
            }
        } else if (specificPeriods == 'thirdQuarter') {
            periodFilteredSet = {
                ...fullSet,
                backgroundColor: fullSet.backgroundColor.slice(6, 9),
                label: fullSet.label.slice(6, 9),
                data: fullSet.data.slice(6, 9),
            }
        } else if (specificPeriods == 'fourthQuarter') {
            periodFilteredSet = {
                ...fullSet,
                backgroundColor: fullSet.backgroundColor.slice(9, 12),
                label: fullSet.label.slice(9, 12),
                data: fullSet.data.slice(9, 12),
            }
        }
    }

    // Finalmente aplicamos el filtro por lastMonthIndex si existe
    if (lastMonthIndex !== null) {
        // Para vista anual, filtrar hasta el lastMonthIndex
        if (period === 'year') {
            const endIndex = lastMonthIndex + 1;
            return {
                ...periodFilteredSet,
                backgroundColor: periodFilteredSet.backgroundColor.slice(0, endIndex),
                label: periodFilteredSet.label.slice(0, endIndex),
                data: periodFilteredSet.data.slice(0, endIndex),
            }
        }
        
        // Para trimestres/semestres, filtrar los meses que están más allá del lastMonthIndex
        // Necesitamos verificar cuál es el índice de inicio del período
        let startIndex = 0;
        if (period === 'semester') {
            startIndex = specificPeriods === 'firstSemester' ? 0 : 6;
        } else if (period === 'quarter') {
            if (specificPeriods === 'firstQuarter') startIndex = 0;
            else if (specificPeriods === 'secondQuarter') startIndex = 3;
            else if (specificPeriods === 'thirdQuarter') startIndex = 6;
            else if (specificPeriods === 'fourthQuarter') startIndex = 9;
        }
        
        // Calcular cuántos meses del período filtrado están dentro del rango válido
        const validMonthsCount = Math.max(0, lastMonthIndex - startIndex + 1);
        
        if (validMonthsCount > 0 && validMonthsCount < periodFilteredSet.data.length) {
            return {
                ...periodFilteredSet,
                backgroundColor: periodFilteredSet.backgroundColor.slice(0, validMonthsCount),
                label: periodFilteredSet.label.slice(0, validMonthsCount),
                data: periodFilteredSet.data.slice(0, validMonthsCount),
            }
        }
    }

    return periodFilteredSet;
}

export function GetAccumulateAmount({ source, isPast = false, lastMonthIndex = null, period = 'year', language = 'es', periodYearLabel }) {
    // El source ya viene filtrado correctamente por GetDataset
    // No necesitamos aplicar filtro adicional aquí
    const filteredData = source.data;
    const filteredLabels = source.label;
    
    if (!filteredLabels || filteredLabels.length === 0) {
        return {
            header: '',
            content: "$ 0",
            footer: '',
            isBack: isPast
        }
    }
    
    const [startPeriod, year] = filteredLabels[0].split('-')
    const [endPeriod] = filteredLabels[filteredLabels.length - 1].split('-')

    const startMonth = formatDashboardMonthCode(startPeriod, language);
    const endMonth = formatDashboardMonthCode(endPeriod, language);
    const completeSet = {
        header: year != 'yyyy' ? (periodYearLabel ? periodYearLabel({ year }) : `PERIODO ${year}`) : '',
        content: "$ 0",
        footer: startPeriod != '000' ? (startPeriod === endPeriod ? startMonth : `${startMonth} - ${endMonth}`) : '',
        isBack: isPast
    }

    const total = Math.round(filteredData.reduce((acc, el) => acc + el, 0) * 1000000)
    const content = formatMoneyWithCurrency(total)
    return {
        ...completeSet,
        content,
    }
}

export function GetCurrentAmount({ source, isPast = false, lastMonthIndex = null, language = 'es' }) {
    // Usar el último mes con datos en lugar del mes actual del calendario
    const monthToUse = lastMonthIndex !== null ? lastMonthIndex : moment().month();
    
    // Verificar que el índice esté dentro del rango
    if (!source[monthToUse]) {
        return {
            header: '',
            content: "$ 0",
            isBack: isPast
        }
    }
    
    const { label, sales } = source[monthToUse]

    const header = label != '000-yyyy' ? formatDashboardLabelPeriod(label, language).toUpperCase() : ''

    const completeSet = {
        header,
        content: "$ 0",
        isBack: isPast
    }

    const total = Math.round(sales * 1000000)
    const content = formatMoneyWithCurrency(total)
    return {
        ...completeSet,
        content,
    }
}

export function GetPorcentage({ data, lastMonthIndex = null, period = 'year', language = 'es' }) {

    const completeSet = {
        porcentage: formatPercentValue(0, language),
        footer: "",
    }
    // El data ya viene filtrado correctamente por GetDataset
    const lastPeriod = GetAccumulateAmount({ source: data[0], lastMonthIndex, period })
    const currentPeriod = GetAccumulateAmount({ source: data[1], lastMonthIndex, period })
    const cleanCurrentAmount = currentPeriod.content.replace(/[^0-9-]/g, '')
    const cleanLastAmount = lastPeriod.content.replace(/[^0-9-]/g, '')

    // Usar los labels que ya vienen filtrados en data
    const lastLabels = data[0].label
    const currentLabels = data[1].label

    const getFooter = (arr) => {
        if (!arr || arr.length === 0) return '';

        const firstLabel = arr[0]
        const lastLabel = arr[arr.length - 1]

        if (firstLabel == '000-yyyy' && lastLabel == '000-yyyy') return '';

        const [startPeriod, year] = firstLabel.split('-')
        const [endPeriod] = lastLabel.split('-')

        // Si es el mismo mes, no mostrar rango
        if (startPeriod === endPeriod) {
            return `${formatDashboardMonthCode(startPeriod, language)} ${year}`
        }

        return `${formatDashboardMonthCode(startPeriod, language)} - ${formatDashboardMonthCode(endPeriod, language)} ${year}`
    }

    const firstPeriod = getFooter(lastLabels);
    const SecondPeriod = getFooter(currentLabels);

    const footer = firstPeriod && SecondPeriod ? `${getFooter(lastLabels)} / ${getFooter(currentLabels)}` : '';
    
    // Calcular porcentaje de variación
    // Fórmula: ((valor actual - valor anterior) / valor anterior) * 100
    let porcentage = 0;
    const currentAmount = parseInt(cleanCurrentAmount) || 0;
    const lastAmount = parseInt(cleanLastAmount) || 0;
    
    if (lastAmount === 0) {
        // Si el año anterior es 0 y el actual tiene valor, es un aumento infinito (mostramos 100% o el valor actual como referencia)
        porcentage = currentAmount > 0 ? 100 : 0;
    } else {
        porcentage = ((currentAmount - lastAmount) / Math.abs(lastAmount)) * 100;
    }

    return {
        ...completeSet,
        porcentage: formatPercentValue(porcentage, language),
        footer,
    }
}

